import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { API_CONFIG } from '../config/api'
import { handleAuthError, handleAccountSuspended } from '../utils/authErrorHandler'
import type {
    ProductFilters,
    CreateAccountRequest,
    CreateContactRequest,
    CreateOpportunityRequest,
    CreateMeetingRequest,
    AiDemoRecommendationRequest,
    CreateProductRequest,
    UpdateProductRequest,
    CreateTrainingAssetRequest,
    UpdateTrainingAssetRequest,
    ApiError
} from '../types/api'

const api = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout
})

// Add Sentry breadcrumbs for failed API calls + handle 401
api.interceptors.response.use(
    (response) => response,
    (error) => {
        Sentry.addBreadcrumb({
            category: 'api',
            message: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`,
            level: error.response?.status >= 500 ? 'error' : 'warning',
            data: {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
            },
        })

        // On 401, clear auth state and redirect to login
        if (error.response?.status === 401) {
            handleAuthError()
        }

        // On 403 ACCOUNT_SUSPENDED, the account was suspended mid-session —
        // sign out and send to login with a message.
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED') {
            handleAccountSuspended()
        }

        return Promise.reject(error)
    }
)

// ============================================
// Auth API (public - no token required)
// ============================================

export async function authLogin(email: string, password: string) {
    const res = await api.post('/api/auth/login', { email, password })
    return res.data
}

export async function authSignup(data: {
    email: string
    password: string
    name: string
    signupType: 'advisor-invite' | 'supplier-claim'
    code: string
    advisorDetails?: { firmName?: string; region?: string; tier?: string }
}) {
    const res = await api.post('/api/auth/signup', data)
    return res.data
}

export async function authForgotPassword(email: string) {
    const res = await api.post('/api/auth/forgot-password', { email })
    return res.data
}

// ============================================
// Health
// ============================================

export async function getHealth() {
    try {
        const res = await api.get('/healthz')
        return res.data
    } catch (error) {
        console.error('Health check failed', error)
        throw error
    }
}

export interface CompetitiveSupplier {
    id: string
    name: string
    logo_url: string | null
    website_url: string | null
    product_count: number
    subcategory_overlap: number
    industry_overlap: number
    scores: { channel_investment: number | null; product_depth: number | null; financial_stability: number | null; rating: number | null; rating_count: number }
    primary_industries: string[]
    primary_segments: string[]
    shared_subcategories?: { id: string; name: string | null }[]
    watched?: boolean
    engagement_30d?: { page_views: number; shortlists: number; comparisons: number }
}
export interface CompetitiveBenchmarks {
    cohort_size: number
    avg: { product_count: number | null; rating: number | null; channel_investment: number | null; product_depth: number | null; financial_stability: number | null }
}
export interface CompetitiveEngagement {
    window_days: number
    self: { page_views: number; shortlists: number; comparisons: number }
    cohort_avg: { page_views: number; shortlists: number; comparisons: number } | null
}
export interface ComparedWithSupplier {
    id: string
    name: string
    logo_url: string | null
    website_url: string | null
    comparison_count: number
}
export interface CompetitiveLandscape {
    self: CompetitiveSupplier
    competitors: CompetitiveSupplier[]
    sharedSubcategories: { id: string; name: string | null }[]
    benchmarks: CompetitiveBenchmarks
    engagement: CompetitiveEngagement
    frequently_compared_with: ComparedWithSupplier[]
}
export async function getCompetitiveLandscape(supplierId: string, token: string): Promise<CompetitiveLandscape> {
    const res = await api.get(`/api/suppliers/${supplierId}/competitive-landscape`, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}

export interface CompareProduct {
    id: string
    name: string
    description: string | null
    marketing_summary: string | null
    value_props: string[]
    seo_keywords: string[]
    pricing_bands: string[]
    target_segments: string[]
    target_industries: string[]
    deployment_model: string | null
    compliance_certifications: string[]
    subcategory_ids: string[]
}
export interface CompareSide {
    id: string
    name: string
    logo_url: string | null
    website_url: string | null
    description: string | null
    scores: CompetitiveSupplier['scores']
    primary_industries: string[]
    primary_segments: string[]
    product_count: number
    coverage: { certs: string[]; segments: string[]; deployments: string[] }
    tier1_qualification: { industry: string; required: string[]; held: string[]; qualifies: boolean }[]
    products: CompareProduct[]
}
export interface CompetitorComparison {
    self: CompareSide
    competitor: CompareSide
    shared_subcategories: { id: string; name: string | null; self_products: CompareProduct[]; competitor_products: CompareProduct[] }[]
    engagement: { self: Record<string, number>; competitor: Record<string, number> }
    window_days: number
}
export interface CompetitorAiAnalysis {
    self: string
    competitor: string
    generated_at: string
    analysis: {
        one_liner: string
        where_you_win: { point: string; evidence: string }[]
        where_they_win: { point: string; evidence: string }[]
        unique_strengths: string[]
        recommendations: { area: string; action: string; why: string }[]
    }
}
export interface WatchlistData {
    suppliers: { item_id: string; created_at: string; supplier: { id: string; name: string; logo_url: string | null; website_url: string | null; aggregate_rating: number | null } }[]
    excluded_suppliers: { item_id: string; created_at: string; supplier: { id: string; name: string; logo_url: string | null; website_url: string | null; aggregate_rating: number | null } }[]
    products: { item_id: string; created_at: string; product: CompareProduct & { supplier: { id: string; name: string; logo_url: string | null; website_url: string | null } } }[]
}
export interface LandscapeProduct extends CompareProduct {
    subcategory_labels: string[]
    supplier: { id: string; name: string; logo_url: string | null; website_url: string | null; aggregate_rating: number | null }
    is_own: boolean
}
export async function searchCompetitiveProducts(supplierId: string, params: { q?: string; subcategory_id?: string }, token: string): Promise<LandscapeProduct[]> {
    const res = await api.get(`/api/suppliers/${supplierId}/competitive-products`, { params, headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export interface ComparableProduct extends LandscapeProduct {
    match: { score: number; shared_subcategories: string[] }
}
export async function getMyCompetitiveProducts(supplierId: string, token: string): Promise<LandscapeProduct[]> {
    const res = await api.get(`/api/suppliers/${supplierId}/competitive-products/mine`, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function getComparableProducts(supplierId: string, productId: string, token: string): Promise<{ own: LandscapeProduct; comparables: ComparableProduct[] }> {
    const res = await api.get(`/api/suppliers/${supplierId}/competitive-products/comparables`, { params: { product_id: productId }, headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function getWatchlist(supplierId: string, token: string): Promise<WatchlistData> {
    const res = await api.get(`/api/suppliers/${supplierId}/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function searchWatchlistSuppliers(supplierId: string, q: string, token: string): Promise<{ id: string; name: string; logo_url: string | null; website_url: string | null; _count: { products: number } }[]> {
    const res = await api.get(`/api/suppliers/${supplierId}/watchlist`, { params: { search: q }, headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function addToWatchlist(supplierId: string, target: { supplier_id?: string; product_id?: string; excluded?: boolean }, token: string): Promise<{ id: string }> {
    const res = await api.post(`/api/suppliers/${supplierId}/watchlist`, target, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function removeFromWatchlist(supplierId: string, itemId: string, token: string): Promise<void> {
    await api.delete(`/api/suppliers/${supplierId}/watchlist/${itemId}`, { headers: { Authorization: `Bearer ${token}` } })
}

export async function getCompetitorComparison(supplierId: string, competitorId: string, token: string): Promise<CompetitorComparison> {
    const res = await api.get(`/api/suppliers/${supplierId}/competitive-landscape/${competitorId}`, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
export async function runCompetitorAiAnalysis(supplierId: string, competitorId: string, token: string): Promise<CompetitorAiAnalysis> {
    const res = await api.post(`/api/suppliers/${supplierId}/competitive-landscape/${competitorId}/analyze`, {}, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}

export async function getSupplierEngagement(supplierId: string, days: number, token: string) {
    const res = await api.get(`/api/suppliers/${supplierId}/engagement`, {
        params: { days },
        headers: { Authorization: `Bearer ${token}` },
    })
    return res.data as { total: number; byType: Record<string, number>; uniqueAdvisors: number; days: number }
}

export async function getSupplierDashboard(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/dashboard`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier dashboard', error)
        throw error
    }
}

// Advisor APIs
export async function getAdvisorSuppliers(advisorId: string, token: string, params?: Record<string, string>) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/suppliers`, {
            headers: { Authorization: `Bearer ${token}` },
            params
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor suppliers', error)
        throw error
    }
}

export async function getAdvisorSupplierProfile(advisorId: string, supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/suppliers/${supplierId}/profile`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor supplier profile', error)
        throw error
    }
}

export async function getAdvisorProducts(advisorId: string, filters: ProductFilters = {}, token: string) {
    try {
        const query = new URLSearchParams(filters as Record<string, string>).toString()
        const res = await api.get(`/api/advisors/${advisorId}/products${query ? '?' + query : ''}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor products', error)
        throw error
    }
}

export async function getAdvisorProductById(advisorId: string, productId: string, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/products/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor product details', error)
        throw error
    }
}

// CRM - Accounts (formerly Companies)
export async function getAccounts(token: string) {
    try {
        const res = await api.get('/api/advisors/accounts', {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch accounts', error)
        throw error
    }
}

export async function createAccount(data: CreateAccountRequest, token: string) {
    try {
        const res = await api.post('/api/advisors/accounts', data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create account', error)
        throw error
    }
}

// Update Account
export async function updateAccount(id: string, data: Partial<CreateAccountRequest>, token: string) {
    try {
        const res = await api.put(`/api/advisors/accounts/${id}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update account', error)
        throw error
    }
}

// Delete Account
export async function deleteAccount(id: string, token: string) {
    try {
        await api.delete(`/api/advisors/accounts/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
    } catch (error) {
        console.error('Failed to delete account', error)
        throw error
    }
}

// Legacy aliases - deprecated, use getAccounts/createAccount
export const getCompanies = getAccounts
export const createCompany = createAccount

// CRM - Contacts
export async function getContacts(token: string) {
    try {
        const res = await api.get('/api/advisors/contacts', {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch contacts', error)
        throw error
    }
}

export async function createContact(data: CreateContactRequest, token: string) {
    try {
        const res = await api.post('/api/advisors/contacts', data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create contact', error)
        throw error
    }
}

// Update Contact
export async function updateContact(id: string, data: Partial<CreateContactRequest>, token: string) {
    try {
        const res = await api.put(`/api/advisors/contacts/${id}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update contact', error)
        throw error
    }
}

// Delete Contact
export async function deleteContact(id: string, token: string) {
    try {
        await api.delete(`/api/advisors/contacts/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
    } catch (error) {
        console.error('Failed to delete contact', error)
        throw error
    }
}

// Unified contacts (account + supplier + TSD)
export async function getAllContacts(token: string) {
    try {
        const res = await api.get('/api/advisors/workspace/contacts/all', {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch unified contacts', error)
        throw error
    }
}

export async function createMeeting(advisorId: string, data: CreateMeetingRequest, token: string) {
    try {
        const res = await api.post(`/api/advisors/${advisorId}/meetings`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create meeting', error)
        throw error
    }
}


export async function getAdvisorTrainingAssets(advisorId: string, params: { q?: string, type?: string, skillLevel?: string, supplierId?: string } = {}, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/training-assets`, {
            params,
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to search advisor training', error)
        throw error
    }
}

export async function getAdvisorAiRecommendation(advisorId: string, scenario: string, token: string) {
    try {
        const res = await api.post(`/api/advisors/${advisorId}/recommendations/demo`, { scenario }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to get AI recommendations', error)
        throw error
    }
}

export async function updateAdvisorProfile(data: any, token: string) {
    try {
        const res = await api.put('/api/advisors/me', data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update advisor profile', error)
        throw error
    }
}

export async function getAdvisorSupplierDetail(advisorId: string, supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/suppliers/${supplierId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor supplier detail', error)
        throw error
    }
}

export async function postAdvisorAiDemoRecommendation(advisorId: string, payload: AiDemoRecommendationRequest, token: string) {
    try {
        const res = await api.post(`/api/advisors/${advisorId}/recommendations/demo`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to get AI recommendations', error)
        throw error
    }
}

// Meetings & AI Companion
export async function getAdvisorMeetings(advisorId: string, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/meetings`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch meetings', error)
        throw error
    }
}

export async function createAdvisorMeeting(advisorId: string, data: { title: string, occurred_at: string, transcript_text: string }, token: string) {
    try {
        const res = await api.post(`/api/advisors/${advisorId}/meetings`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create meeting', error)
        throw error
    }
}

export async function renameAdvisorMeeting(advisorId: string, meetingId: string, title: string, token: string) {
    const res = await api.patch(`/api/advisors/${advisorId}/meetings/${meetingId}`, { title }, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deleteAdvisorMeeting(advisorId: string, meetingId: string, token: string) {
    const res = await api.delete(`/api/advisors/${advisorId}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function queryAdvisorAi(advisorId: string, query: string, token: string) {
    try {
        const res = await api.post(`/api/advisors/${advisorId}/ai/query`, { query }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to query AI', error)
        throw error
    }
}

export async function unifiedSearch(advisorId: string, query: string, token: string) {
    try {
        const res = await api.get(`/api/advisors/${advisorId}/search`, {
            params: { q: query },
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to perform unified search', error)
        throw error
    }
}

export async function getSupplierProfile(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/profile`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier profile', error)
        throw error
    }
}

export async function updateSupplierProfile(supplierId: string, data: { description?: string, website_url?: string, direct_quote_url?: string, logo_url?: string }, token: string) {
    try {
        const res = await api.put(`/api/suppliers/${supplierId}/profile`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update supplier profile', error)
        throw error
    }
}

export async function getSupplierEmployees(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/employees`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier employees', error)
        throw error
    }
}

export async function getSupplierProducts(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/products`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier products', error)
        throw error
    }
}

export async function getSupplierProduct(supplierId: string, productId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/products/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier product', error)
        throw error
    }
}

export async function createSupplierProduct(supplierId: string, data: CreateProductRequest, token: string) {
    try {
        const res = await api.post(`/api/suppliers/${supplierId}/products`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create supplier product', error)
        throw error
    }
}

export async function updateSupplierProduct(supplierId: string, productId: string, data: UpdateProductRequest, token: string) {
    try {
        const res = await api.put(`/api/suppliers/${supplierId}/products/${productId}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update supplier product', error)
        throw error
    }
}

export async function deleteSupplierProduct(supplierId: string, productId: string, token: string) {
    try {
        await api.delete(`/api/suppliers/${supplierId}/products/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
    } catch (error) {
        console.error('Failed to delete supplier product', error)
        throw error
    }
}

export async function getSupplierTrainingAssets(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/training-assets`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier training assets', error)
        throw error
    }
}

export async function createSupplierTrainingAsset(supplierId: string, data: CreateTrainingAssetRequest, token: string) {
    try {
        const res = await api.post(`/api/suppliers/${supplierId}/training-assets`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create training asset', error)
        throw error
    }
}

export async function updateSupplierTrainingAsset(supplierId: string, assetId: string, data: UpdateTrainingAssetRequest, token: string) {
    try {
        const res = await api.put(`/api/suppliers/${supplierId}/training-assets/${assetId}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update training asset', error)
        throw error
    }
}

export async function deleteSupplierTrainingAsset(supplierId: string, assetId: string, token: string) {
    await api.delete(`/api/suppliers/${supplierId}/training-assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
}

export async function getSupplierPromotions(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/promotions`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier promotions', error)
        throw error
    }
}

export async function getFeedConfig(supplierId: string, token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/feed-config`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch feed config', error)
        throw error
    }
}

export async function updateFeedConfig(supplierId: string, url: string, token: string) {
    try {
        const res = await api.put(`/api/suppliers/${supplierId}/feed-config`, { url }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update feed config', error)
        throw error
    }
}

export async function triggerFeedSync(supplierId: string, token: string) {
    try {
        const res = await api.post(`/api/suppliers/${supplierId}/feed-sync`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to sync feed', error)
        throw error
    }
}



// Metrics Catalog APIs
export async function getSupplierMetricsCatalog(params: { priority?: string; theme?: string; search?: string }, token: string) {
    try {
        const searchParams = new URLSearchParams()
        if (params.priority) searchParams.append('priority', params.priority)
        if (params.theme) searchParams.append('theme', params.theme)
        if (params.search) searchParams.append('search', params.search)

        const res = await api.get(`/api/metrics/catalog/supplier?${searchParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch supplier metrics catalog', error)
        throw error
    }
}

export async function getAdvisorMetricsCatalog(params: { priority?: string; theme?: string; search?: string }, token: string) {
    try {
        const searchParams = new URLSearchParams()
        if (params.priority) searchParams.append('priority', params.priority)
        if (params.theme) searchParams.append('theme', params.theme)
        if (params.search) searchParams.append('search', params.search)

        const res = await api.get(`/api/metrics/catalog/advisor?${searchParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch advisor metrics catalog', error)
        throw error
    }
}

export async function getSupplierMetricSamples(supplierId: string, metricIds: string[], token: string) {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/metrics/sample?metricIds=${metricIds.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch metric samples', error)
        throw error
    }
}

export function getClaims(token: string) {
    if (!token) return {};
    try {
        const parts = token.split('.');
        if (parts.length < 2) return {};
        const base64Url = parts[1]!;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.warn('Failed to decode token', e);
        return {};
    }
}

export async function getTaxonomy() {
    try {
        const res = await api.get('/api/taxonomy')
        return res.data
    } catch (error) {
        console.error('Failed to fetch taxonomy', error)
        throw error
    }
}

// Canonical industry list (matches the account-landscape categorization).
export async function getIndustries(): Promise<string[]> {
    try {
        const res = await api.get('/api/taxonomy/industries')
        return res.data
    } catch (error) {
        console.error('Failed to fetch industries', error)
        return []
    }
}

// CRM - Opportunities (formerly Deals)
export async function getOpportunities(token: string) {
    try {
        const res = await api.get('/api/advisors/opportunities', {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to fetch opportunities', error)
        throw error
    }
}

export async function createOpportunity(data: CreateOpportunityRequest, token: string) {
    try {
        const res = await api.post('/api/advisors/opportunities', data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to create opportunity', error)
        throw error
    }
}

// Update Opportunity
export async function updateOpportunity(id: string, data: Partial<CreateOpportunityRequest>, token: string) {
    try {
        const res = await api.put(`/api/advisors/opportunities/${id}`, data, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to update opportunity', error)
        throw error
    }
}

// Delete Opportunity
export async function deleteOpportunity(id: string, token: string) {
    try {
        await api.delete(`/api/advisors/opportunities/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
    } catch (error) {
        console.error('Failed to delete opportunity', error)
        throw error
    }
}

/**
 * Link a meeting to an opportunity
 * Used when creating an opportunity from AI analysis or attaching a meeting to existing opportunity
 */
export async function linkMeetingToOpportunity(meetingId: string, opportunityId: string, token: string) {
    try {
        const res = await api.put(`/api/advisors/me/meetings/${meetingId}/link-opportunity`, {
            opportunityId
        }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to link meeting to opportunity', error)
        throw error
    }
}

// Legacy aliases - deprecated, use getOpportunities/createOpportunity
export const getDeals = getOpportunities
export const createDeal = createOpportunity

// ============================================
// AUTH APIs
// ============================================

export interface UserProfile {
    id: string
    email: string
    name: string | null
    role: 'ADMIN' | 'ADVISOR' | 'SUPPLIER_ADMIN' | 'SUPPLIER_USER' | 'CHANNEL_MANAGER'
    status: 'PENDING' | 'ACTIVE' | 'SUSPENDED'
    mustChangePassword?: boolean
    termsAcceptedAt?: string | null
    termsVersionAccepted?: string | null
    createdAt: string
    advisor?: {
        id: string
        firmName: string | null
        region: string | null
        tier: 'FREE' | 'PROFESSIONAL'
        trialEndsAt: string | null
        onboardingCompletedAt: string | null
        onboardingSkippedAt: string | null
        onboardingStep: string | null
        pipelineStages?: Array<{ value: string; label: string; probability: number; visible: boolean }>
        filterByTsdDefault?: boolean
        emailDigestFrequency?: 'realtime' | 'daily' | 'weekly' | 'none'
        phone?: string | null
        tsdNames?: string[]
        logoUrl?: string | null
        brandColor?: string | null
        brandingEnabled?: boolean
    }
    supplierEmployee?: {
        id: string
        jobTitle: string | null
        permissionLevel: 'OWNER' | 'ADMIN' | 'USER' | 'MDF'
        supplier: {
            id: string
            name: string
            slug: string
            tier: 'FREE' | 'BASIC' | 'ENTERPRISE'
            accountStatus: 'STUB' | 'CLAIMED' | 'ACTIVE' | 'SUSPENDED'
            onboardingCompletedAt: string | null
            onboardingSkippedAt: string | null
            onboardingStep: string | null
        } | null
    }
    channelManager?: {
        id: string
        title: string | null
        supplierId: string | null
        supplierName: string | null
        supplierNameRaw: string | null
        onboardedAt: string | null
        settings: {
            cadenceAnchorDays: number
            cadenceScalingDays: number
            cadenceBuildingDays: number
            cadenceLaunchingDays: number
            territoryRegion: string | null
            territoryExceptions: string[]
        } | null
    }
}

export interface AuthCallbackResponse {
    user?: UserProfile
    error?: string
    needsSignup?: boolean
}

export async function authCallback(email: string, name: string | null, token: string): Promise<AuthCallbackResponse> {
    try {
        const res = await api.post('/api/auth/callback', { email, name }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error: unknown) {
        const apiError = error as ApiError
        if (apiError.response?.status === 404) {
            return { needsSignup: true }
        }
        console.error('Auth callback failed', error)
        throw error
    }
}

export async function getMe(token: string): Promise<AuthCallbackResponse> {
    try {
        const res = await api.get('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error: unknown) {
        const apiError = error as ApiError
        if (apiError.response?.status === 404) {
            return { needsSignup: true }
        }
        console.error('Get me failed', error)
        throw error
    }
}

export async function acceptTerms(token: string): Promise<{ ok: boolean; version: string }> {
    const res = await api.post('/api/auth/accept-terms', {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export interface CompleteOauthSignupPayload {
    email: string
    role: 'ADVISOR' | 'CHANNEL_MANAGER'
    name?: string
    firmName?: string
    region?: string
    title?: string
    supplierId?: string
    supplierNameRaw?: string
    territoryRegion?: 'NE' | 'SE' | 'MW' | 'SW' | 'W'
}

export async function completeOauthSignup(payload: CompleteOauthSignupPayload, token: string): Promise<{ user: UserProfile }> {
    const res = await api.post('/api/auth/complete-oauth-signup', payload, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function changeMyPassword(newPassword: string, token: string, currentPassword?: string): Promise<{ success: boolean }> {
    const res = await api.patch('/api/auth/me/password', { newPassword, currentPassword }, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function signupAdvisor(data: {
    email: string
    name: string | null
    firmName?: string
    region?: string
    tier?: 'FREE' | 'PROFESSIONAL'
    inviteCode?: string
}, token: string): Promise<{ user: UserProfile }> {
    const res = await api.post('/api/auth/signup/advisor', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function signupSupplier(data: {
    email: string
    name: string | null
    companyName: string
    tier?: 'FREE' | 'BASIC' | 'ENTERPRISE'
}, token: string): Promise<{ user: UserProfile }> {
    const res = await api.post('/api/auth/signup/supplier', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function validateClaimCode(code: string): Promise<{
    valid: boolean
    error?: string
    supplier?: { id: string; name: string }
}> {
    const res = await api.get(`/api/auth/claim/validate/${code}`)
    return res.data
}

export async function validateAdvisorInviteCode(code: string): Promise<{
    valid: boolean
    error?: string
}> {
    const res = await api.get(`/api/auth/advisor-invite/validate/${code}`)
    return res.data
}

export async function claimSupplier(data: {
    email: string
    name: string | null
    claimCode: string
}, token: string): Promise<{ user: UserProfile }> {
    const res = await api.post('/api/auth/claim', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function acceptInvite(data: {
    email: string
    name: string | null
    inviteToken: string
}, token: string): Promise<{ user: UserProfile }> {
    const res = await api.post('/api/auth/invite/accept', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// Quote API Functions (Phase 9)
// ============================================

import type { Quote, QuoteListResponse, CreateQuoteRequest, CompleteQuoteRequest } from '../types/api'

// Advisor Quote APIs
export async function getAdvisorQuotes(
    advisorId: string,
    params: { status?: string; limit?: number; offset?: number } = {},
    token: string
): Promise<QuoteListResponse> {
    const res = await api.get(`/api/advisors/${advisorId}/quotes`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function getAdvisorQuote(advisorId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.get(`/api/advisors/${advisorId}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function createAdvisorQuote(advisorId: string, data: CreateQuoteRequest, token: string): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function updateAdvisorQuote(
    advisorId: string,
    quoteId: string,
    data: { configuration?: Record<string, unknown>; advisorNotes?: string },
    token: string
): Promise<Quote> {
    const res = await api.patch(`/api/advisors/${advisorId}/quotes/${quoteId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function submitAdvisorQuote(advisorId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function sendAdvisorQuote(advisorId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function acceptAdvisorQuote(advisorId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function rejectAdvisorQuote(
    advisorId: string,
    quoteId: string,
    data: { reason?: string },
    token: string
): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/reject`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function cancelAdvisorQuote(advisorId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function addAdvisorQuoteMessage(
    advisorId: string,
    quoteId: string,
    data: { message: string },
    token: string
): Promise<{ id: string; message: string; created_at: string }> {
    const res = await api.post(`/api/advisors/${advisorId}/quotes/${quoteId}/message`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// Supplier Quote APIs
export async function getSupplierQuotes(
    supplierId: string,
    params: { status?: string; limit?: number; offset?: number } = {},
    token: string
): Promise<QuoteListResponse> {
    const res = await api.get(`/api/suppliers/${supplierId}/quotes`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function getSupplierQuote(supplierId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.get(`/api/suppliers/${supplierId}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function getSupplierQuoteStats(supplierId: string, token: string): Promise<{
    pendingQuotes: number
    completedThisMonth: number
}> {
    const res = await api.get(`/api/suppliers/${supplierId}/quotes/stats`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function acknowledgeSupplierQuote(supplierId: string, quoteId: string, token: string): Promise<Quote> {
    const res = await api.post(`/api/suppliers/${supplierId}/quotes/${quoteId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function completeSupplierQuote(
    supplierId: string,
    quoteId: string,
    data: CompleteQuoteRequest,
    token: string
): Promise<Quote> {
    const res = await api.post(`/api/suppliers/${supplierId}/quotes/${quoteId}/complete`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function clarifySupplierQuote(
    supplierId: string,
    quoteId: string,
    data: { message: string },
    token: string
): Promise<Quote> {
    const res = await api.post(`/api/suppliers/${supplierId}/quotes/${quoteId}/clarify`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function addSupplierQuoteMessage(
    supplierId: string,
    quoteId: string,
    data: { message: string },
    token: string
): Promise<{ id: string; message: string; created_at: string }> {
    const res = await api.post(`/api/suppliers/${supplierId}/quotes/${quoteId}/message`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADMIN - Advisor Invite Codes
// ============================================

export interface AdvisorInviteCode {
    id: string
    code: string
    status: 'AVAILABLE' | 'USED'
    created_at: string
    used_at: string | null
    used_by_email: string | null
}

export interface AdvisorInviteCodeListResponse {
    codes: AdvisorInviteCode[]
    pagination: {
        page: number
        limit: number
        total: number
        pages: number
    }
}

export async function generateAdvisorInviteCodes(count: number, token: string): Promise<{ codes: Array<{ id: string; code: string; created_at: string }> }> {
    try {
        const res = await api.post('/api/admin/advisor-invite-codes', { count }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to generate advisor invite codes', error)
        throw error
    }
}

export async function listAdvisorInviteCodes(
    params: { status?: string; page?: number; limit?: number },
    token: string
): Promise<AdvisorInviteCodeListResponse> {
    try {
        const searchParams = new URLSearchParams()
        if (params.status) searchParams.append('status', params.status)
        if (params.page) searchParams.append('page', params.page.toString())
        if (params.limit) searchParams.append('limit', params.limit.toString())

        const res = await api.get(`/api/admin/advisor-invite-codes?${searchParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to list advisor invite codes', error)
        throw error
    }
}

export async function sendAdvisorInviteCode(codeId: string, email: string, token: string): Promise<{ success: boolean }> {
    try {
        const res = await api.post(`/api/admin/advisor-invite-codes/${codeId}/send`, { email }, {
            headers: { Authorization: `Bearer ${token}` }
        })
        return res.data
    } catch (error) {
        console.error('Failed to send advisor invite code', error)
        throw error
    }
}

// ============================================
// ADMIN - Direct User Creation
// ============================================

export async function adminCreateAdvisor(
    data: { email: string; name: string; firmName?: string; region?: string; tier?: 'FREE' | 'PROFESSIONAL' },
    token: string
): Promise<{ user: { id: string; email: string; name: string }; advisor: { id: string; tier: string }; tempPassword: string }> {
    const res = await api.post('/api/admin/create-advisor', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function adminCreateSupplierUser(
    data: { email: string; name: string; supplierId: string; permissionLevel?: 'OWNER' | 'ADMIN' | 'USER' | 'MDF' },
    token: string
): Promise<{ user: { id: string; email: string; name: string }; supplierEmployee: unknown; supplierName: string; tempPassword: string }> {
    const res = await api.post('/api/admin/create-supplier-user', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function adminSearchSuppliers(
    search: string,
    token: string
): Promise<{ suppliers: Array<{ id: string; name: string; account_status: string; tier: string }> }> {
    const res = await api.get('/api/admin/suppliers', {
        params: { search, limit: 20, page: 1 },
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ONBOARDING APIs
// ============================================

export async function uploadOnboardingTranscript(formData: FormData, token: string) {
    const res = await api.post('/api/onboarding/advisor/upload-transcript', formData, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    })
    return res.data
}

export async function updateAdvisorOnboarding(
    data: { step?: string; complete?: boolean; skip?: boolean; reset?: boolean },
    token: string
) {
    const res = await api.put('/api/onboarding/advisor', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function updateSupplierOnboarding(
    data: { step?: string; complete?: boolean; skip?: boolean },
    token: string
) {
    const res = await api.put('/api/onboarding/supplier', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADVISOR - Supplier Preferences
// ============================================

export async function fetchSupplierPreferences(token: string) {
    const res = await api.get('/api/advisors/me/supplier-preferences', {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function upsertSupplierPreference(
    supplierId: string,
    data: { is_favorite?: boolean; is_blocked?: boolean; is_direct_contract?: boolean },
    token: string
) {
    const res = await api.put(`/api/advisors/me/supplier-preferences/${supplierId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deleteSupplierPreference(supplierId: string, token: string) {
    const res = await api.delete(`/api/advisors/me/supplier-preferences/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADVISOR - Contact Preferences
// ============================================

export async function fetchContactPreferences(token: string) {
    const res = await api.get('/api/advisors/me/contact-preferences', {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function upsertContactPreference(
    contactId: string,
    data: { is_favorite?: boolean; notes?: string },
    token: string
) {
    const res = await api.put(`/api/advisors/me/contact-preferences/${contactId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deleteContactPreference(contactId: string, token: string) {
    const res = await api.delete(`/api/advisors/me/contact-preferences/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADVISOR - Private Supplier Contacts
// ============================================

export async function fetchMyPrivateContacts(token: string) {
    const res = await api.get('/api/advisors/me/supplier-contacts', {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function addPrivateContact(
    data: { supplier_id: string; name: string; email: string; job_title?: string; phone?: string },
    token: string
) {
    const res = await api.post('/api/advisors/me/supplier-contacts', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function updatePrivateContact(
    contactId: string,
    data: { name?: string; email?: string; job_title?: string | null; phone?: string | null },
    token: string
) {
    const res = await api.put(`/api/advisors/me/supplier-contacts/${contactId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deletePrivateContact(contactId: string, token: string) {
    const res = await api.delete(`/api/advisors/me/supplier-contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADVISOR - Stale Contact Reports
// ============================================

export async function reportStaleContact(
    data: { supplier_employee_id: string; reason?: string },
    token: string
) {
    const res = await api.post('/api/advisors/me/contact-reports', data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADVISOR - Supplier Contacts (enhanced)
// ============================================

export async function fetchSupplierContactsForAdvisor(supplierId: string, token: string) {
    const res = await api.get(`/api/advisors/me/suppliers/${supplierId}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// ADMIN - Solution Requirements
// ============================================

import type { SolutionRequirement, RequirementSuggestion, EffectiveRequirement, ProductRequirementOverride } from '../types/requirements'

export async function getSubcategoryRequirements(subcategoryId: string, token: string): Promise<SolutionRequirement[]> {
    const res = await api.get(`/api/admin/taxonomy/subcategories/${subcategoryId}/requirements`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function createSubcategoryRequirement(
    subcategoryId: string,
    data: { name: string; label: string; field_type: string; options?: string[]; description?: string; is_required?: boolean },
    token: string
): Promise<SolutionRequirement> {
    const res = await api.post(`/api/admin/taxonomy/subcategories/${subcategoryId}/requirements`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function updateRequirement(
    reqId: string,
    data: Partial<SolutionRequirement>,
    token: string
): Promise<SolutionRequirement> {
    const res = await api.put(`/api/admin/taxonomy/requirements/${reqId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deleteRequirement(reqId: string, token: string): Promise<void> {
    await api.delete(`/api/admin/taxonomy/requirements/${reqId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
}

export async function reorderRequirements(subcategoryId: string, orderedIds: string[], token: string): Promise<void> {
    await api.put(`/api/admin/taxonomy/subcategories/${subcategoryId}/requirements/reorder`, { orderedIds }, {
        headers: { Authorization: `Bearer ${token}` }
    })
}

export async function generateRequirements(subcategoryId: string, token: string): Promise<RequirementSuggestion[]> {
    const res = await api.post(`/api/admin/taxonomy/subcategories/${subcategoryId}/requirements/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

// ============================================
// SUPPLIER - Product Requirements
// ============================================

export async function getProductEffectiveRequirements(supplierId: string, productId: string, token: string): Promise<EffectiveRequirement[]> {
    const res = await api.get(`/api/suppliers/${supplierId}/products/${productId}/requirements`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function getProductRequirementOverrides(supplierId: string, productId: string, token: string): Promise<ProductRequirementOverride[]> {
    const res = await api.get(`/api/suppliers/${supplierId}/products/${productId}/requirement-overrides`, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function upsertProductRequirementOverride(
    supplierId: string,
    productId: string,
    data: Partial<ProductRequirementOverride> & { action: string },
    token: string
): Promise<ProductRequirementOverride> {
    const res = await api.post(`/api/suppliers/${supplierId}/products/${productId}/requirement-overrides`, data, {
        headers: { Authorization: `Bearer ${token}` }
    })
    return res.data
}

export async function deleteProductRequirementOverride(supplierId: string, productId: string, overrideId: string, token: string): Promise<void> {
    await api.delete(`/api/suppliers/${supplierId}/products/${productId}/requirement-overrides/${overrideId}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
}

export default api
